import { useState } from 'react'
import { Box, ChartTip, Stack, Text } from '@ui'
import { UNITS } from '../format.js'
import { niceTicks, useWidth } from './chartUtils.js'
import { aheadText, fmt } from './YearChart.jsx'

// Distance per year or per month over a chosen span, one bar each, with a tick across the bar
// where that period has a target. The period still in progress is drawn lighter.

const HEIGHT = 240
const MARGIN = { top: 12, right: 8, bottom: 40, left: 52 }
const GAP = 2
const RADIUS = 4

// rows: [{ key, label, axisLabel, minorLabel?, meters, target, partial }], oldest first. By month,
// minorLabel is the month's letter (shown when there's room) and axisLabel the year under January.
export default function HistoryChart({ rows, unit }) {
  const [wrapRef, width] = useWidth()
  const [hover, setHover] = useState(null)
  const u = UNITS[unit].meters

  const top = Math.max(u, ...rows.map((r) => Math.max(r.meters, r.target ?? 0))) * 1.06
  const innerW = width - MARGIN.left - MARGIN.right
  const innerH = HEIGHT - MARGIN.top - MARGIN.bottom
  const band = innerW / Math.max(1, rows.length)
  const barW = Math.max(1, Math.min(48, band - GAP))
  const y = (meters) => MARGIN.top + innerH - (meters / top) * innerH
  const yTicks = niceTicks(0, top / u, 4)
  const baseline = MARGIN.top + innerH
  // By year, thin out the labels so they never collide (about 34px each). By month, years sit on
  // a second line under January, so they're 12 bars apart; letters only show with room for them.
  const byMonth = rows.some((r) => r.minorLabel)
  const labelEvery = byMonth ? 1 : Math.max(1, Math.ceil(34 / band))
  const showMinor = band >= 12

  const hr = hover != null ? rows[hover] : null
  const hx = hover != null ? MARGIN.left + band * hover + band / 2 : 0

  return (
    <Box ref={wrapRef} position="relative">
      <Stack wrap gap={3} text="small" muted mb={1}>
        <span><Text display="inline-block" me={1} rounded={1} style={{ width: 12, height: 10, background: 'var(--chart-distance)' }} />Walked</span>
        {rows.some((r) => r.partial) && (
          <span><Text display="inline-block" me={1} rounded={1} style={{ width: 12, height: 10, background: 'var(--chart-distance)', opacity: 0.45 }} />So far (still in progress)</span>
        )}
        {rows.some((r) => r.target) && (
          <span><Text display="inline-block" me={1} valign="middle" style={{ width: 14, height: 2, background: 'var(--wl-emphasis)' }} />Target</span>
        )}
      </Stack>
      <svg
        width={width} height={HEIGHT} role="img"
        aria-label="Distance walked per period; the table below has the same figures"
        onPointerLeave={() => setHover(null)}
        style={{ touchAction: 'pan-y', display: 'block' }}
      >
        {yTicks.map((t) => (
          <g key={t}>
            <line x1={MARGIN.left} x2={width - MARGIN.right} y1={y(t * u)} y2={y(t * u)} stroke="var(--chart-grid)" strokeWidth="1" />
            <text x={MARGIN.left - 6} y={y(t * u)} dy="0.32em" textAnchor="end" fill="var(--wl-muted)" style={{ fontSize: 11 }}>
              {t.toLocaleString('en-GB')}
            </text>
          </g>
        ))}
        {rows.map((r, i) => {
          const cx = MARGIN.left + band * i + band / 2
          const left = cx - barW / 2
          const h = baseline - y(r.meters)
          const radius = Math.min(RADIUS, barW / 2, h)
          return (
            <g key={r.key}>
              {h > 0 && (
                <path
                  d={`M${left},${baseline}V${baseline - h + radius}Q${left},${baseline - h} ${left + radius},${baseline - h}` +
                    `H${left + barW - radius}Q${left + barW},${baseline - h} ${left + barW},${baseline - h + radius}V${baseline}Z`}
                  fill="var(--chart-distance)" fillOpacity={r.partial ? 0.45 : hover === i ? 0.85 : 1}
                />
              )}
              {r.target && (
                <line x1={left - 1} x2={left + barW + 1} y1={y(r.target)} y2={y(r.target)}
                  stroke="var(--wl-emphasis)" strokeWidth="2" />
              )}
              {byMonth && showMinor && (
                <text x={cx} y={baseline + 14} textAnchor="middle" fill="var(--wl-muted)" style={{ fontSize: 10 }}>
                  {r.minorLabel}
                </text>
              )}
              {r.axisLabel && i % labelEvery === 0 && (
                <text x={byMonth ? left : cx} y={byMonth ? HEIGHT - 6 : baseline + 16} textAnchor={byMonth ? 'start' : 'middle'}
                  fill="var(--wl-muted)" style={{ fontSize: 11 }}>
                  {r.axisLabel}
                </text>
              )}
              {/* Hit target: the whole column, wider than the bar. */}
              <rect x={MARGIN.left + band * i} y={MARGIN.top} width={band} height={innerH} fill="transparent"
                onPointerEnter={() => setHover(i)} onPointerDown={() => setHover(i)} />
            </g>
          )
        })}
      </svg>

      {hr && (
        <ChartTip
          style={{ top: 30, left: Math.max(0, Math.min(hx + 10, width - 190)), pointerEvents: 'none', minWidth: 170 }}
        >
          <Box weight="semibold">{hr.label}{hr.partial ? ' (so far)' : ''}</Box>
          <div>Walked {fmt(hr.meters / u, 1)} {UNITS[unit].label}</div>
          {hr.target && <Box muted>Target {fmt(hr.target / u, 1)} {UNITS[unit].label}</Box>}
          {hr.target && !hr.partial && <div>{aheadText(hr.meters - hr.target, unit)}</div>}
        </ChartTip>
      )}
    </Box>
  )
}
